import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismCheckbox,
  PrismInput,
  PrismModal,
  PrismSelectInput,
} from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import { t } from 'ember-intl';
import isValidEmailAddress from 'frontend-utils/utils/is-valid-email-address';

interface ModalInviteUsersSignature {
  Args: {
    onClose: () => void;
  };
}

interface SelectOption extends Option {
  id: string;
}

function parseEmails(value: string): string[] {
  if (!value.length) {
    return [];
  }

  const trimmedValues = value
    .split(';')
    .map((email) => email.trim())
    .filter((email) => email.length > 0);

  return [...new Set(trimmedValues)];
}

export default class ModalInviteUsers extends Component<ModalInviteUsersSignature> {
  @service declare intl: Services['intl'];

  @tracked addToScheduledReports = false;
  @tracked emails = '';
  @tracked selectedProduct: SelectOption | null =
    this.productOptions[0] ?? null;
  @tracked selectedProperty: SelectOption | null = null;
  @tracked selectedRole: SelectOption | null = this.roleOptions[0] ?? null;

  get emailErrorMessage(): string | undefined {
    if (this.emails.length > 0 && this.hasInvalidEmail) {
      return this.intl.t('settings.reports.error.invalid_email');
    }
    return undefined;
  }

  get emailsArray(): string[] {
    return parseEmails(this.emails);
  }

  get hasEmailError(): boolean {
    return this.emailErrorMessage !== undefined;
  }

  get hasInvalidEmail(): boolean {
    return this.emailsArray.some((email) => !isValidEmailAddress(email));
  }

  get isSubmitDisabled(): boolean {
    return (
      this.emailsArray.length === 0 ||
      this.hasInvalidEmail ||
      this.selectedProperty === null ||
      this.selectedRole === null ||
      this.selectedProduct === null
    );
  }

  get productOptions(): SelectOption[] {
    return [
      { id: 'all', label: this.intl.t('settings.users.invite.products_all') },
      {
        id: 'bi_benchmark',
        label: this.intl.t('settings.users.column.bi_benchmarking'),
      },
      {
        id: 'rate_insight_market_insight',
        label: this.intl.t('settings.users.column.rate_insight_market_insight'),
      },
    ];
  }

  // Property options will be populated when API integration is added
  get propertyOptions(): SelectOption[] {
    return [];
  }

  get roleOptions(): SelectOption[] {
    return [
      { id: 'admin', label: this.intl.t('useraccess_rights_admin') },
      { id: 'write', label: this.intl.t('useraccess_rights_write') },
      { id: 'read', label: this.intl.t('useraccess_rights_read') },
    ];
  }

  @action
  submit(): void {
    // No API integration yet — will be wired in a future ticket
    this.args.onClose();
  }

  @action
  toggleScheduledReports(): void {
    this.addToScheduledReports = !this.addToScheduledReports;
  }

  @action
  updateEmails(event: Event): void {
    this.emails = (event.target as HTMLInputElement).value;
  }

  @action
  updateProduct(option: SelectOption): void {
    this.selectedProduct = option;
  }

  @action
  updateProperty(option: SelectOption): void {
    this.selectedProperty = option;
  }

  @action
  updateRole(option: SelectOption): void {
    this.selectedRole = option;
  }

  <template>
    <PrismModal
      @onClose={{@onClose}}
      @title={{t "settings.users.invite.title"}}
    >
      <:default>
        <div class="flex flex-col gap-400 p-500">
          <PrismInput
            @errorMessage={{this.emailErrorMessage}}
            @hasErrors={{this.hasEmailError}}
            @helpText={{t "settings.users.invite.email_helper"}}
            @label={{t "settings.users.invite.email_label"}}
            @onInput={{this.updateEmails}}
            @removePadding={{true}}
            @required={{true}}
            @textarea={{true}}
            @value={{this.emails}}
          />

          <div class="flex flex-col gap-100">
            <span class="text-200--bold">
              {{t "settings.users.invite.property_label"}}
            </span>
            <PrismSelectInput
              @isSecondary={{true}}
              @onChange={{this.updateProperty}}
              @options={{this.propertyOptions}}
              @placeholder={{t "settings.users.invite.property_label"}}
              @unsafe_matchTriggerWidth={{true}}
              @value={{this.selectedProperty}}
            />
          </div>

          <div class="flex flex-col gap-100">
            <span class="text-200--bold">
              {{t "settings.users.invite.role_label"}}
            </span>
            <PrismSelectInput
              @isSecondary={{true}}
              @onChange={{this.updateRole}}
              @options={{this.roleOptions}}
              @placeholder={{t "settings.users.invite.role_label"}}
              @unsafe_matchTriggerWidth={{true}}
              @value={{this.selectedRole}}
            />
          </div>

          <div class="flex flex-col gap-100">
            <span class="text-200--bold">
              {{t "settings.users.invite.products_label"}}
            </span>
            <PrismSelectInput
              @isSecondary={{true}}
              @onChange={{this.updateProduct}}
              @options={{this.productOptions}}
              @placeholder={{t "settings.users.invite.products_label"}}
              @unsafe_matchTriggerWidth={{true}}
              @value={{this.selectedProduct}}
            />
          </div>

          <div class="flex flex-col gap-200">
            <span class="text-200--bold">
              {{t "settings.users.invite.scheduled_reports_label"}}
            </span>
            <PrismCheckbox
              @checked={{this.addToScheduledReports}}
              @onChange={{this.toggleScheduledReports}}
            >
              {{t "settings.users.invite.scheduled_reports_checkbox"}}
            </PrismCheckbox>
          </div>
        </div>
      </:default>

      <:footer>
        <div class="flex h-full items-center justify-end gap-400 px-500">
          <PrismButton
            @buttonType="secondary"
            @label={{t "general.cancel"}}
            @onClick={{@onClose}}
          />
          <PrismButton
            @isDisabled={{this.isSubmitDisabled}}
            @label={{t "settings.users.invite_users"}}
            @onClick={{this.submit}}
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
